import React, { useEffect, useId, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Check, ChevronDown } from 'lucide-react';

export interface ComposerSelectOption<Value extends string> {
  id: Value;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface ComposerSelectProps<Value extends string> {
  title: string;
  value: Value;
  options: readonly ComposerSelectOption<Value>[];
  onChange: (value: Value) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  fallbackLabel?: string;
}

/** The composer model, mode and access selectors share this exact surface. */
export function ComposerSelect<Value extends string>({
  title, value, options, onChange, open, onOpenChange, disabled = false, fallbackLabel,
}: ComposerSelectProps<Value>) {
  const areaRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const selected = options.find((option) => option.id === value);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: MouseEvent) => {
      if (!areaRef.current?.contains(event.target as Node)) onOpenChange(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (disabled && open) onOpenChange(false);
  }, [disabled, open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const selectedRow = menu?.querySelector<HTMLButtonElement>('[aria-checked="true"]:not(:disabled)');
    (selectedRow ?? menu?.querySelector<HTMLButtonElement>('button:not(:disabled)'))?.focus();
  }, [open]);

  const close = () => {
    onOpenChange(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        onOpenChange(true);
        return;
      }
      const rows = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
      const current = rows.indexOf(document.activeElement as HTMLButtonElement);
      const next = (current + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length;
      rows[next]?.focus();
    }
  };

  return (
    <ReboModeArea
      ref={areaRef}
      onKeyDown={handleKeyDown}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) onOpenChange(false);
      }}
    >
      <ReboModeTrigger
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`${title}: ${selected?.label ?? fallbackLabel ?? value}`}
        onClick={() => onOpenChange(!open)}
        $open={open}
      >
        <ReboModeLabel>{selected?.label ?? fallbackLabel ?? value}</ReboModeLabel>
        <ChevronDown size={12} aria-hidden="true" />
      </ReboModeTrigger>
      {open && (
        <ReboModePopup ref={menuRef} id={menuId} role="menu" aria-label={title}>
          <ReboModePopupHeader>{title}</ReboModePopupHeader>
          {options.map((option) => (
            <ReboModeRow
              key={option.id}
              type="button"
              role="menuitemradio"
              aria-checked={value === option.id}
              $active={value === option.id}
              $disabled={option.disabled}
              disabled={disabled || option.disabled}
              onClick={() => {
                close();
                onChange(option.id);
              }}
            >
              <ReboModeRowInfo>
                <ReboModeRowName>{option.label}</ReboModeRowName>
                {option.description && <ReboModeRowDesc>{option.description}</ReboModeRowDesc>}
              </ReboModeRowInfo>
              {value === option.id && !option.disabled && <ReboModeCheck><Check size={16} aria-hidden="true" /></ReboModeCheck>}
            </ReboModeRow>
          ))}
        </ReboModePopup>
      )}
    </ReboModeArea>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const ReboModeArea = styled.div`
  position: relative;
`;

const ReboModeTrigger = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 2px;
  border-radius: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:disabled { opacity: 0.45; cursor: default; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.accent}; outline-offset: 2px; }

  ${({ $open, theme }) =>
    $open &&
    css`
      color: ${theme.colors.textPrimary};
    `}

  svg {
    transition: transform 0.15s ease;
    ${({ $open }) => $open && css`transform: rotate(180deg);`}
  }
`;

const ReboModeLabel = styled.span`
  line-height: 1;
`;

const ReboModePopup = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: min(220px, calc(100vw - 32px));
  max-width: min(320px, calc(100vw - 32px));
  padding: 6px;
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(40, 40, 42, 0.96)'
      : 'rgba(255, 255, 255, 0.96)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)'};
  z-index: 10;
  animation: ${fadeIn} 0.15s ease;
`;

const ReboModePopupHeader = styled.div`
  padding: 6px 10px 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.02em;
`;

const ReboModeRow = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  border-radius: 10px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.05)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  }

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      opacity: 0.45;
      cursor: not-allowed;
      color: ${theme.colors.textMuted};

      &:hover {
        background: transparent;
      }
    `}
`;

const ReboModeRowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ReboModeRowName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
`;

const ReboModeRowDesc = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.3;
  margin-top: 1px;
`;

const ReboModeCheck = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.accent};
  display: flex;
  align-items: center;
`;
